import { api, type Uuid } from "@shared/api";

type Kind = "text" | "voice" | "announcement";

/** Шаблон сервера — как в Discord: готовые категории и каналы под повод. */
export interface ServerTemplate {
  id: string;
  title: string;
  /** Знак на плитке шаблона. */
  sign: string;
  /** `null` — свой: стартовые каналы сервера остаются как есть. */
  layout: { category: string; channels: [Kind, string][] }[] | null;
}

const info = (extra: [Kind, string][] = []) => ({
  category: "Информация",
  channels: [["text", "добро-пожаловать"], ["announcement", "объявления"], ...extra] as [Kind, string][],
});

export const SERVER_TEMPLATES: ServerTemplate[] = [
  { id: "own", title: "Свой", sign: "✨", layout: null },
  {
    id: "games",
    title: "Игры",
    sign: "🎮",
    layout: [
      info(),
      { category: "Общение", channels: [["text", "общий"], ["text", "клипы-и-скрины"]] },
      { category: "Игры", channels: [["text", "поиск-тиммейтов"], ["voice", "Лобби"], ["voice", "Игровая 1"], ["voice", "Игровая 2"]] },
    ],
  },
  {
    id: "friends",
    title: "Друзья",
    sign: "💬",
    layout: [
      { category: "Текстовые каналы", channels: [["text", "общий"], ["text", "планы"], ["text", "мемы"]] },
      { category: "Голосовые каналы", channels: [["voice", "Гостиная"], ["voice", "Игры"]] },
    ],
  },
  {
    id: "study",
    title: "Учёба",
    sign: "📚",
    layout: [
      info(),
      { category: "Учёба", channels: [["text", "общий"], ["text", "домашка"], ["text", "заметки"], ["text", "вопросы"]] },
      { category: "Голосовые каналы", channels: [["voice", "Читальный зал"], ["voice", "Перерыв"]] },
    ],
  },
  {
    id: "club",
    title: "Клуб",
    sign: "🎲",
    layout: [
      info([["text", "события"]]),
      { category: "Общение", channels: [["text", "общий"], ["text", "знакомство"]] },
      { category: "Голосовые каналы", channels: [["voice", "Встреча"], ["voice", "Свободный"]] },
    ],
  },
  {
    id: "creators",
    title: "Творчество",
    sign: "🎨",
    layout: [
      info(),
      { category: "Работы", channels: [["text", "показ-работ"], ["text", "отзывы"], ["text", "вдохновение"]] },
      { category: "Голосовые каналы", channels: [["voice", "Мастерская"], ["voice", "Стрим"]] },
    ],
  },
  {
    id: "local",
    title: "Сообщество",
    sign: "🏡",
    layout: [
      info(),
      { category: "Город", channels: [["text", "общий"], ["text", "события"], ["text", "барахолка"], ["text", "помощь"]] },
      { category: "Голосовые каналы", channels: [["voice", "Площадь"]] },
    ],
  },
];

/**
 * Создаёт сервер по шаблону: сначала раскладку шаблона, потом убирает
 * стартовые каналы — так сервер ни мгновения не стоит пустым. Отвечает, какой
 * канал открыть первым.
 */
export async function createFromTemplate(name: string, template: ServerTemplate): Promise<{ server: Uuid; channel: Uuid | null }> {
  const created = await api.servers.create(name);
  const server = created.server.id;
  if (!template.layout) return { server, channel: null };

  /*
   * Сервер уже есть: сбой посреди раскладки не должен выглядеть как «не
   * создан» — повторное нажатие завело бы второй. Открываем то, что вышло;
   * стартовые каналы тогда остаются на месте.
   */
  let first: Uuid | null = null;
  try {
    for (const group of template.layout) {
      const category = await api.categories.create(server, group.category);
      for (const [channel_type, channelName] of group.channels) {
        const channel = await api.channels.create(server, { name: channelName, channel_type, category_id: category.id });
        if (!first && channel_type === "text") first = channel.id;
      }
    }
    for (const channel of created.channels) await api.channels.remove(channel.id);
    for (const category of created.categories) await api.categories.remove(category.id);
  } catch {
    return { server, channel: first };
  }
  return { server, channel: first };
}
